import React, { useState } from 'react';
import { BsChevronLeft } from 'react-icons/bs';

import { Button } from 'ui/button/button.ui';

import { useAppSelector } from 'store/hooks';
import { selectConsumerUnitList } from 'store/consumer_unit/consumer_unit.selector';
import { createServiceRequestService } from 'services/service_requests.service';

import {
  Field,
  Lead,
  Notice,
  StatusTag,
  StepTag,
  WizardBody,
  WizardFooter,
} from './setup-wizard.styles';

export type UnlistedKind = 'inverter' | 'utility';

interface UnlistedSetupRequestProps {
  kind: UnlistedKind;
  defaultConsumerUnitId?: string;
  onBack: () => void;
  onClose: () => void;
}

const TEXT = {
  inverter: {
    title: 'Meu inversor não aparece',
    lead: 'Nosso suporte configura a conexão a partir do seu caso e avisa por e-mail quando a geração começar a entrar.',
    nameLabel: 'Marca do inversor',
    namePlaceholder: 'Ex.: Deye, Solis, Hoymiles',
    siteLabel: 'App ou site em que você vê a geração',
    sitePlaceholder: 'Ex.: Solarman, SolisCloud',
    unitLabel: 'Imóvel da usina',
    notesPlaceholder:
      'Ex.: o instalador criou a conta no meu e-mail. Consigo entrar no app normalmente.',
    done: (name: string, unit: string) =>
      `O suporte vai analisar o inversor ${name} de ${unit} e responder no seu e-mail de cadastro. Enquanto isso, você pode informar a geração ao salvar cada fatura.`,
  },
  utility: {
    title: 'Minha distribuidora não aparece',
    lead: 'O suporte configura a leitura a partir da sua fatura e avisa por e-mail quando o recebimento automático estiver pronto.',
    nameLabel: 'Distribuidora',
    namePlaceholder: 'Ex.: CEMIG, EDP, Energisa',
    siteLabel: 'Site em que você baixa a fatura (opcional)',
    sitePlaceholder: 'Ex.: atende.cemig.com.br',
    unitLabel: 'Imóvel',
    notesPlaceholder: 'Algo que ajude o suporte (opcional).',
    done: (name: string, unit: string) =>
      `O suporte vai analisar a ${name} usando a fatura de ${unit} e responder no seu e-mail de cadastro. Enquanto isso, continue importando o PDF da fatura.`,
  },
};

/**
 * Pedido ao suporte — "Meu inversor / Minha distribuidora não aparece na
 * lista" (P6 do assistente de configuração, out/2026; só planos pagos — o gate
 * de verdade é o backend). Vira uma solicitação `inversor_nao_listado` /
 * `distribuidora_nao_listada` na fila de /admin/servicos, com e-mail à equipe
 * (a distribuidora leva a última fatura do imóvel anexada).
 */
export const UnlistedSetupRequest = ({
  kind,
  defaultConsumerUnitId,
  onBack,
  onClose,
}: UnlistedSetupRequestProps) => {
  const text = TEXT[kind];
  const consumerUnitList = useAppSelector(selectConsumerUnitList);

  const [name, setName] = useState('');
  const [site, setSite] = useState('');
  const [unitId, setUnitId] = useState(
    defaultConsumerUnitId ?? consumerUnitList[0]?.id ?? ''
  );
  const [notes, setNotes] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const unitName =
    consumerUnitList.find((u) => u.id === unitId)?.name ?? 'seu imóvel';

  const submit = async () => {
    if (!name.trim()) {
      setError(
        `Informe ${
          kind === 'inverter' ? 'a marca do inversor' : 'a distribuidora'
        }.`
      );
      return;
    }
    if (!unitId) {
      setError('Escolha o imóvel.');
      return;
    }
    setError(null);
    setSending(true);
    try {
      await createServiceRequestService({
        serviceType:
          kind === 'inverter'
            ? 'inversor_nao_listado'
            : 'distribuidora_nao_listada',
        consumerUnitId: unitId,
        notes: notes.trim() || undefined,
        payload:
          kind === 'inverter'
            ? { brand: name.trim(), appName: site.trim() }
            : { utilityName: name.trim(), website: site.trim() },
      });
      setSent(true);
    } catch (err) {
      setError(
        (err as { message?: string })?.message ||
          'Não foi possível enviar o pedido. Tente de novo.'
      );
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <>
        <WizardBody>
          <StepTag>Pedido enviado</StepTag>
          <h3>Recebemos seu pedido</h3>
          <Lead>{text.done(name.trim(), unitName)}</Lead>
          <StatusTag tone="warn">em análise</StatusTag>
        </WizardBody>
        <WizardFooter>
          <span />
          <Button type="button" onClick={onClose}>
            Concluir
          </Button>
        </WizardFooter>
      </>
    );
  }

  return (
    <>
      <WizardBody>
        <StepTag>Pedido ao suporte</StepTag>
        <h3>{text.title}</h3>
        <Lead>{text.lead}</Lead>

        <Field>
          {text.nameLabel}
          <input
            value={name}
            placeholder={text.namePlaceholder}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <Field>
          {text.siteLabel}
          <input
            value={site}
            placeholder={text.sitePlaceholder}
            onChange={(e) => setSite(e.target.value)}
          />
        </Field>
        <Field>
          {text.unitLabel}
          <select value={unitId} onChange={(e) => setUnitId(e.target.value)}>
            {consumerUnitList.length === 0 && <option value="">—</option>}
            {consumerUnitList.map((uc) => (
              <option key={uc.id} value={uc.id}>
                {uc.name} (UC {uc.installation})
              </option>
            ))}
          </select>
        </Field>
        <Field>
          Observação (opcional)
          <textarea
            value={notes}
            placeholder={text.notesPlaceholder}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Field>

        <Notice tone="warn">
          <p>
            Não envie senhas por aqui. Se precisarmos de acesso, o suporte
            explica como fazer com segurança.
          </p>
        </Notice>
        {error && (
          <Notice tone="error">
            <p>{error}</p>
          </Notice>
        )}
      </WizardBody>
      <WizardFooter>
        <Button variant="secondary" type="button" onClick={onBack}>
          <BsChevronLeft />
          Voltar
        </Button>
        <Button type="button" onClick={submit} disabled={sending}>
          {sending ? 'Enviando…' : 'Enviar pedido'}
        </Button>
      </WizardFooter>
    </>
  );
};
